import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  IFilter,
  UTCDateRange,
  UTCDeliveryPeriod,
} from '@epicuro-next/ataraxia/models';
import { selectISDSelectableDaysInThePast } from '@epicuro-next/state/p-basket-state';
import {
  getISODate,
  getISOInterval,
  intervalISODateToDateRange,
  isEqual,
  makeIntervalFromDates,
  makeIntervalISODate,
} from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Filter } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, withLatestFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'epc-products-filter-date-range',
  templateUrl: './products-filter-date-range.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        min-width: 18rem;
      }
    `,
  ],
  standalone: false,
})
export class ProductsFilterDateRangeComponent implements OnInit {
  @Input() public filter: Filter;

  @Input() public set activeFilters(values: IFilter[]) {
    if (values.length) {
      if (this.dateRangeControl.value) {
        const currentIntervalValue = getISOInterval(
          makeIntervalFromDates(
            this.dateRangeControl.value[0],
            this.dateRangeControl.value[1],
          ),
        );
        // prevent set form value on every cdr as it will reset the calendar during USer manipulation
        if (values[0].value === currentIntervalValue) {
          return;
        }
      }

      const { from, to } = intervalISODateToDateRange(
        values[0].value,
      ) as Required<UTCDeliveryPeriod>;

      this.dateRangeControl.setValue([new Date(from), new Date(to)], {
        emitEvent: false,
      });
      this.dateRange$.next({ from, to });

      return;
    }

    if (!values.length && !!this.dateRangeControl.value) {
      this.dateRangeControl.reset();
      this.dateRange$.next(null);
    }
  }

  @Output() public dateRangeChanged = new EventEmitter<IFilter>();

  public dateRangeControl: FormControl<Date[]> = new FormControl();

  public minDate$ = this.store.pipe(
    select(selectISDSelectableDaysInThePast),
    map((days: number) => {
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() - days);
      return currentDate;
    }),
  );

  public dateRange$ = new BehaviorSubject<UTCDateRange | null>(null);

  constructor(private store: Store) {}

  public ngOnInit(): void {
    this.dateRangeControl.valueChanges
      .pipe(
        map((value) => {
          if (!value || !value.length) {
            return null;
          }
          return {
            from: getISODate(value[0]),
            to: getISODate(value[1]),
          };
        }),
        withLatestFrom(this.dateRange$),
        // do not dispatch value if it is not changed (User opens calendar and press Select)
        filter(([value, dateRange]) => {
          return !isEqual(value, dateRange);
        }),
      )
      .subscribe(([value]) => {
        this.dateRange$.next(
          value
            ? {
                from: value.from,
                to: value.to,
              }
            : null,
        );
        const fieldId = this.filter.fieldId;
        const dataType = this.filter.dataType ?? undefined;
        this.dateRangeChanged.emit({
          fieldId,
          dataType,
          value: value ? makeIntervalISODate(value.from, value.to) : '',
        });
      });
  }
}
